import React, { useState, useMemo, useEffect } from 'react';
import {
  Card, Button, Table, Modal, Form, Input, Select, Space, Tag,
  Badge, Row, Col, Statistic, Typography, DatePicker,
  Tooltip, Divider, Timeline, Alert, Popconfirm,
  InputNumber,
} from 'antd';
import { staticApi } from '../utils/staticApi';
import { resolveOwnerRecordKey } from '../utils/resolveOwnerKeys';
import {
  PlusOutlined, WarningOutlined, CheckCircleOutlined,
  SendOutlined, EditOutlined,
  DeleteOutlined, SearchOutlined, EyeOutlined, ExclamationCircleOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCondoStore } from '../store/condoStore';
import { useAuthStore } from '../store/authStore';
import { Violation, Owner } from '../types';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

type ViolationStatus = 'open' | 'notice_sent' | 'resolved' | 'escalated';
type ViolationSeverity = 'low' | 'medium' | 'high' | 'critical';

const CATEGORIES = [
  'Noise Complaint', 'Parking Violation', 'Pet Policy', 'Garbage/Recycling',
  'Balcony Use', 'Common Area Misuse', 'Unauthorized Alteration',
  'Lease Violation', 'Unauthorized Subletting', 'Smoking', 'Other',
];

const STATUS_CONFIG: Record<ViolationStatus, { color: string; label: string; icon: React.ReactNode }> = {
  open:        { color: 'orange',  label: 'Open',         icon: <ExclamationCircleOutlined /> },
  notice_sent: { color: 'blue',    label: 'Notice Sent',  icon: <SendOutlined /> },
  resolved:    { color: 'green',   label: 'Resolved',     icon: <CheckCircleOutlined /> },
  escalated:   { color: 'red',     label: 'Escalated',    icon: <WarningOutlined /> },
};

const SEVERITY_CONFIG: Record<ViolationSeverity, { color: string; label: string }> = {
  low:      { color: 'green',  label: 'Low' },
  medium:   { color: 'gold',   label: 'Medium' },
  high:     { color: 'orange', label: 'High' },
  critical: { color: 'red',    label: 'Critical' },
};

function genViolationNumber(): string {
  return `VIO-${dayjs().format('YYYY')}-${String(Math.floor(1000 + Math.random() * 9000))}`;
}

export const Violations: React.FC = () => {
  // Sprint 5e — narrow selectors instead of whole-store destructure.
  // See pages/fees/useFeesData.ts for the canonical pattern.
  const owners                = useCondoStore((s: any) => s.owners);
  const violations            = useCondoStore((s: any) => s.violations);
  const fetchViolations       = useCondoStore((s: any) => s.fetchViolations);
  const createViolation       = useCondoStore((s: any) => s.createViolation);
  const updateViolation       = useCondoStore((s: any) => s.updateViolation);
  const deleteViolation       = useCondoStore((s: any) => s.deleteViolation);
  const sendViolationNotice   = useCondoStore((s: any) => s.sendViolationNotice);
  const resolveViolation      = useCondoStore((s: any) => s.resolveViolation);
  const addViolationNote      = useCondoStore((s: any) => s.addViolationNote);
  const applyViolationFine    = useCondoStore((s: any) => s.applyViolationFine);
  const markViolationFinePaid = useCondoStore((s: any) => s.markViolationFinePaid);
  const { currentCondo } = useAuthStore();
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchViolations(currentCondo?.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetchViolations is a stable zustand action
  }, [currentCondo?.id]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState<Violation | null>(null);
  const [editing, setEditing] = useState<Violation | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [noteText, setNoteText] = useState('');
  const [form] = Form.useForm();
  const [fineModalOpen, setFineModalOpen] = useState(false);
  const [finingViolation, setFiningViolation] = useState<Violation | null>(null);
  const [fineForm] = Form.useForm();

  const filtered = useMemo(() => violations.filter((v: Violation) => {
    const matchText = !search ||
      v.violationNumber.toLowerCase().includes(search.toLowerCase()) ||
      v.unitNumber.toLowerCase().includes(search.toLowerCase()) ||
      v.ownerName.toLowerCase().includes(search.toLowerCase()) ||
      v.category.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus || v.status === filterStatus;
    return matchText && matchStatus;
  }), [violations, search, filterStatus]);

  const stats = useMemo(() => ({
    total: violations.length,
    open: violations.filter((v: Violation) => v.status === 'open').length,
    escalated: violations.filter((v: Violation) => v.status === 'escalated').length,
    resolved: violations.filter((v: Violation) => v.status === 'resolved').length,
  }), [violations]);

  const handleOpen = (v?: Violation) => {
    setEditing(v ?? null);
    if (v) {
      // Sprint 27 — finding N1. DatePicker takes a Dayjs instance; the
      // entity stores dates as 'YYYY-MM-DD' strings.
      form.setFieldsValue({
        ...v,
        reportedDate: v.reportedDate ? dayjs(v.reportedDate) : null,
        dueDate: v.dueDate ? dayjs(v.dueDate) : null,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        violationNumber: genViolationNumber(),
        reportedDate: dayjs(),
        dueDate: dayjs().add(14, 'day'),
        status: 'open',
        severity: 'medium',
        fineIssued: false,
      });
    }
    setModalOpen(true);
  };

  // Sprint 136 (ADR-131) — the Select value is the canonical owner key, so look
  // the record back up by that key (see the options binding below).
  const handleOwnerSelect = (ownerId: string) => {
    const owner = owners.find((o: Owner) => resolveOwnerRecordKey(o) === ownerId);
    if (owner) {
      form.setFieldsValue({ unitNumber: owner.unitNumber, ownerName: owner.name });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const normalized = {
        ...values,
        severity: values.severity?.toUpperCase(),
        status: values.status?.toUpperCase(),
        // Sprint 27 — finding N1. DatePicker emits Dayjs; backend
        // (and the rest of the store) expects 'YYYY-MM-DD'. Normalize
        // here so the rest of the codebase stays unchanged.
        reportedDate: values.reportedDate?.format
          ? values.reportedDate.format('YYYY-MM-DD')
          : values.reportedDate,
        dueDate: values.dueDate?.format
          ? values.dueDate.format('YYYY-MM-DD')
          : values.dueDate,
      };
      if (editing) {
        await updateViolation(editing.id, normalized);
        staticApi.message.success('Violation updated');
      } else {
        await createViolation({
          notes: [],
          photos: [],
          noticeSentDate: undefined,
          resolvedDate: undefined,
          fineIssued: false,
          condoId: currentCondo?.id || '',
          ...normalized,
        });
        staticApi.message.success(`Violation ${values.violationNumber} logged`);
      }
      setModalOpen(false);
    } catch (err: any) {
      // Sprint 89A — surface server failures (was silent). Severity/status are
      // already normalized to UPPERCASE above and violationNumber/ownerName are
      // included, so a 4xx here is a real server error worth showing. AntD
      // validation rejections carry `errorFields` and render inline.
      if (!err?.errorFields) {
        staticApi.message.error(err?.message || 'Failed to log violation. Please try again.');
      }
    }
  };

  const handleSendNotice = async (v: Violation) => {
    try {
      await sendViolationNotice(v.id);
      staticApi.message.success(`Notice sent to ${v.ownerName} for violation ${v.violationNumber}`);
      if (selected?.id === v.id) setSelected(violations.find((x: Violation) => x.id === v.id) ?? null);
    } catch {
      staticApi.message.error('Failed to send notice');
    }
  };

  const handleResolve = async (v: Violation) => {
    try {
      await resolveViolation(v.id);
      staticApi.message.success(`Violation ${v.violationNumber} marked as resolved`);
      if (selected?.id === v.id) setSelected(violations.find((x: Violation) => x.id === v.id) ?? null);
    } catch {
      staticApi.message.error('Failed to resolve violation');
    }
  };

  const handleOpenFine = (v: Violation) => {
    setFiningViolation(v);
    fineForm.resetFields();
    fineForm.setFieldsValue({ amount: v.fineAmount || 50 });
    setFineModalOpen(true);
  };

  const handleApplyFine = async () => {
    try {
      const values = await fineForm.validateFields();
      await applyViolationFine(finingViolation!.id, values.amount);
      staticApi.message.success(`Fine of $${values.amount} applied to ${finingViolation!.violationNumber}`);
      setFineModalOpen(false);
    } catch { /* validation */ }
  };

  const handleMarkFinePaid = async (v: Violation) => {
    await markViolationFinePaid(v.id);
    staticApi.message.success(`Fine for ${v.violationNumber} marked as paid`);
  };

  const handleAddNote = async (v: Violation) => {
    if (!noteText.trim()) return;
    try {
      await addViolationNote(v.id, noteText.trim());
      const updated = violations.find((x: Violation) => x.id === v.id);
      if (updated) setSelected(updated);
      setNoteText('');
    } catch {
      staticApi.message.error('Failed to add note');
    }
  };

  const columns = [
    { title: 'Violation No.', dataIndex: 'violationNumber', key: 'num', render: (v: string) => <Tag color="purple">{v}</Tag> },
    { title: 'Unit', dataIndex: 'unitNumber', key: 'unit', render: (v: string) => <strong>Unit {v}</strong> },
    { title: 'Owner', dataIndex: 'ownerName', key: 'owner' },
    { title: 'Category', dataIndex: 'category', key: 'cat', render: (v: string) => <Tag>{v}</Tag> },
    {
      title: 'Severity', dataIndex: 'severity', key: 'sev',
      render: (v: ViolationSeverity) => <Tag color={SEVERITY_CONFIG[v].color}>{SEVERITY_CONFIG[v].label}</Tag>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (v: ViolationStatus) => <Badge status={v === 'resolved' ? 'success' : v === 'escalated' ? 'error' : v === 'notice_sent' ? 'processing' : 'warning'} text={STATUS_CONFIG[v].label} />,
    },
    {
      title: 'Fine',
      key: 'fine',
      width: 120,
      render: (_: any, r: Violation) => {
        if (!r.fineIssued || !r.fineAmount) return <Tag color="default">No Fine</Tag>;
        return (r as any).finePaid
          ? <Tag color="green" icon={<DollarOutlined />}>${r.fineAmount} Paid</Tag>
          : <Tag color="red" icon={<DollarOutlined />}>${r.fineAmount} Unpaid</Tag>;
      },
    },
    // Sprint 27 — finding S3. Date cells used to break across lines
    // ("Apr 26, 202 6") because the rendered text was a bare string the
    // table allowed to wrap. nowrap on the cell keeps the formatted date
    // atomic; the column shrinks horizontally in narrow tables instead
    // of slicing the year into pieces.
    { title: 'Reported', dataIndex: 'reportedDate', key: 'date',
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{dayjs(v).format('MMM D, YYYY')}</span> },
    { title: 'Due', dataIndex: 'dueDate', key: 'due', render: (v: string) => {
      const overdue = dayjs(v).isBefore(dayjs()) ;
      return <Text type={overdue ? 'danger' : undefined} style={{ whiteSpace: 'nowrap' }}>{dayjs(v).format('MMM D, YYYY')}</Text>;
    }},
    {
      title: 'Actions', key: 'actions',
      render: (_: any, r: Violation) => (
        <Space>
          <Tooltip title="View Details"><Button size="small" icon={<EyeOutlined />} onClick={() => { setSelected(r); setDetailOpen(true); }} /></Tooltip>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => handleOpen(r)} /></Tooltip>
          {r.status === 'open' && <Tooltip title="Send Notice"><Button size="small" icon={<SendOutlined />} onClick={() => handleSendNotice(r)} /></Tooltip>}
          {r.status !== 'resolved' && <Tooltip title="Mark Resolved"><Button size="small" icon={<CheckCircleOutlined />} onClick={() => handleResolve(r)} /></Tooltip>}
          {!r.fineIssued && <Tooltip title="Apply Fine"><Button size="small" icon={<DollarOutlined />} onClick={() => handleOpenFine(r)} /></Tooltip>}
          {r.fineIssued && !(r as any).finePaid && <Tooltip title="Mark Fine Paid"><Button size="small" type="dashed" icon={<DollarOutlined />} style={{ color: '#52c41a', borderColor: '#52c41a' }} onClick={() => handleMarkFinePaid(r)} /></Tooltip>}
          <Popconfirm title="Delete this violation?" onConfirm={() => deleteViolation(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>
          <WarningOutlined style={{ marginRight: 10, color: '#fa8c16' }} />
          Violation Tracking
        </Title>
        <Text type="secondary">Log, track, and resolve bylaw & rule violations across all units</Text>
      </div>

      <Row gutter={16} style={{ marginBottom: 20 }}>
        {[
          { title: 'Total Violations', value: stats.total, color: '#1890ff' },
          { title: 'Open', value: stats.open, color: '#fa8c16' },
          { title: 'Escalated', value: stats.escalated, color: '#ff4d4f' },
          { title: 'Resolved', value: stats.resolved, color: '#52c41a' },
        ].map(s => (
          <Col span={6} key={s.title}>
            <Card><Statistic title={s.title} value={s.value} valueStyle={{ color: s.color }} /></Card>
          </Col>
        ))}
      </Row>

      <Card
        title="All Violations"
        extra={
          <Space>
            <Input placeholder="Search…" prefix={<SearchOutlined />} value={search} onChange={e => setSearch(e.target.value)} style={{ width: 200 }} allowClear />
            <Select placeholder="All statuses" allowClear style={{ width: 160 }} value={filterStatus || undefined} onChange={v => setFilterStatus(v ?? '')}>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => <Option key={k} value={k}>{v.label}</Option>)}
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpen()}>Log Violation</Button>
          </Space>
        }
      >
        <Table columns={columns} dataSource={filtered} rowKey="id" pagination={{ pageSize: 10 }} scroll={{ x: 1000 }} />
      </Card>

      {/* Create / Edit Modal */}
      <Modal open={modalOpen} onCancel={() => setModalOpen(false)} title={editing ? 'Edit Violation' : 'Log New Violation'} onOk={handleSubmit} width={680} okText={editing ? 'Save Changes' : 'Log Violation'}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}><Form.Item name="violationNumber" label="Violation Number" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}>
              <Form.Item name="ownerId" label="Owner / Unit">
                <Select showSearch placeholder="Select owner" optionFilterProp="label" onChange={handleOwnerSelect}
                  options={owners.map((o: Owner) => ({ label: `Unit ${o.unitNumber} — ${o.name}`, value: resolveOwnerRecordKey(o) }))} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="unitNumber" label="Unit Number" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="ownerName" label="Owner Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="category" label="Violation Category" rules={[{ required: true }]}>
                <Select>{CATEGORIES.map(c => <Option key={c} value={c}>{c}</Option>)}</Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="severity" label="Severity" rules={[{ required: true }]}>
                <Select>{Object.entries(SEVERITY_CONFIG).map(([k, v]) => <Option key={k} value={k}><Tag color={v.color}>{v.label}</Tag></Option>)}</Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}><TextArea rows={3} placeholder="Describe the violation in detail…" /></Form.Item>
          <Form.Item name="location" label="Location / Area"><Input placeholder="e.g. Unit 204 Balcony, Parking Level B" /></Form.Item>
          <Row gutter={16}>
            {/* Sprint 27 — finding N1. Native <input type="date"> renders
                in the browser's locale (MM/DD/YYYY in en-US, DD/MM/YYYY in
                en-CA, ISO elsewhere) which is ambiguous on Canadian seed
                data. DatePicker enforces YYYY-MM-DD everywhere and stays
                consistent with the other modals already using it. */}
            <Col span={8}><Form.Item name="reportedDate" label="Reported Date" rules={[{ required: true }]}><DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="dueDate" label="Compliance Due Date" rules={[{ required: true }]}><DatePicker format="YYYY-MM-DD" style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={8}><Form.Item name="fineAmount" label="Fine Amount ($)"><Input type="number" min={0} /></Form.Item></Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="status" label="Status"><Select>{Object.entries(STATUS_CONFIG).map(([k, v]) => <Option key={k} value={k}>{v.label}</Option>)}</Select></Form.Item></Col>
          </Row>
        </Form>
      </Modal>

      {/* Apply Fine Modal */}
      <Modal
        title={<span><DollarOutlined /> Apply Fine — {finingViolation?.violationNumber}</span>}
        open={fineModalOpen}
        onOk={handleApplyFine}
        onCancel={() => setFineModalOpen(false)}
        okText="Apply Fine"
        width={360}
        destroyOnHidden
      >
        <Form form={fineForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="amount" label="Fine Amount ($)" rules={[{ required: true, message: 'Enter fine amount' }]}>
            <InputNumber style={{ width: '100%' }} min={1} precision={2} placeholder="50.00" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail Drawer */}
      <Modal open={detailOpen} onCancel={() => setDetailOpen(false)} title={`Violation ${selected?.violationNumber}`} footer={[
        <Button key="close" onClick={() => setDetailOpen(false)}>Close</Button>,
        selected?.status !== 'resolved' && <Button key="resolve" type="primary" icon={<CheckCircleOutlined />} onClick={async () => { if (selected) { await handleResolve(selected); setDetailOpen(false); } }}>Mark Resolved</Button>,
      ]} width={700}>
        {selected && (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}><Statistic title="Unit" value={`Unit ${selected.unitNumber}`} /></Col>
              <Col span={8}><Statistic title="Owner" value={selected.ownerName} /></Col>
              <Col span={8}>
                <div><Text type="secondary">Status</Text></div>
                <Badge status={selected.status === 'resolved' ? 'success' : 'warning'} text={STATUS_CONFIG[selected.status].label} />
              </Col>
            </Row>
            <Divider />
            <Paragraph><strong>Category:</strong> {selected.category}</Paragraph>
            <Paragraph><strong>Severity:</strong> <Tag color={SEVERITY_CONFIG[selected.severity].color}>{SEVERITY_CONFIG[selected.severity].label}</Tag></Paragraph>
            <Paragraph><strong>Description:</strong> {selected.description}</Paragraph>
            {selected.location && <Paragraph><strong>Location:</strong> {selected.location}</Paragraph>}
            <Paragraph><strong>Reported:</strong> {dayjs(selected.reportedDate).format('MMMM D, YYYY')}</Paragraph>
            <Paragraph><strong>Compliance Due:</strong> {dayjs(selected.dueDate).format('MMMM D, YYYY')}</Paragraph>
            {selected.fineAmount && <Paragraph><strong>Fine:</strong> ${selected.fineAmount}</Paragraph>}
            {selected.noticeSentDate && <Alert message={`Notice sent on ${dayjs(selected.noticeSentDate).format('MMM D, YYYY HH:mm')}`} type="info" showIcon style={{ marginBottom: 12 }} />}
            {selected.resolvedDate && <Alert message={`Resolved on ${dayjs(selected.resolvedDate).format('MMM D, YYYY HH:mm')}`} type="success" showIcon style={{ marginBottom: 12 }} />}
            <Divider plain>Activity Notes</Divider>
            {selected.notes.length === 0 ? <Text type="secondary">No notes yet.</Text> : (
              <Timeline>
                {selected.notes.map(n => (
                  <Timeline.Item key={n.id} label={dayjs(n.date).format('MMM D HH:mm')}>
                    <Text strong>{n.author}: </Text>{n.text}
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
            <Space.Compact style={{ width: '100%', marginTop: 8 }}>
              <Input placeholder="Add a note…" value={noteText} onChange={e => setNoteText(e.target.value)} onPressEnter={() => handleAddNote(selected)} />
              <Button type="primary" onClick={() => handleAddNote(selected)}>Add</Button>
            </Space.Compact>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Violations;
